/**
 * MODULE: platform/browser/frontend/components/BrowserModule.tsx
 * PURPOSE: Root split-pane layout — missions, CRM deep-links, and live automation.
 * DEPENDENCIES: useBrowserSession, useBrowserChat, useBrowserSessionPoll, browserLaunch, browserSessionPersistence
 * PUBLIC_API: BrowserModule
 * SIDE_EFFECTS: Remote session create/resume/destroy; SSE act stream
 * AGENT_SAFETY: RESTRICTED
 * RECENT: 2026-06-17 [2.2.1437] — Poll patches liveViewFallbackUrl for screencast toggle.
 * RECENT: 2026-06-17 [2.2.1434] — Stale resume shows reconnect UI; reconcile on 404; skip expired sessionStorage.
 * RECENT_PRIOR: 2026-06-17 [2.2.1416] — Wider desktop command panel (`md:basis-[400px]`).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  BROWSER_MISSIONS,
  BROWSER_QUICK_START_URL,
  getBrowserMission,
  promptsForStartUrl,
  resolveMissionStartUrl,
  type BrowserMission,
} from '../data/browserMissions';
import { useBrowserChat } from '../hooks/useBrowserChat';
import { useBrowserCommandInput } from '../hooks/useBrowserCommandInput';
import { useBrowserSession } from '../hooks/useBrowserSession';
import { useBrowserSessionPoll } from '../hooks/useBrowserSessionPoll';
import { useBrowserCapability, isBrowserCapabilityEnabled } from '../hooks/useBrowserCapability';
import { useBrowserProviderStatus } from '../hooks/useBrowserProviderStatus';
import { useIsMobile } from '@shared/hooks';
import { apiV1Url, getAuthHeaders } from '@shared/lib/frontend/api/config';
import type { BrowserSession } from '../types/browser.types';
import { consumeBrowserLaunch } from '../utils/browserLaunch';
import {
  needsServerEmbedLiveUrlRefresh,
  pickEmbedLiveViewUrl,
} from '../utils/browserLiveViewUrl';
import {
  readPersistedBrowserSession,
  persistBrowserSession,
} from '../utils/browserSessionPersistence';
import {
  formatBrowserSessionError,
  isMaxSessionsError,
  isRecoverableBrowserError,
  isSteelChromiumLaunchError,
} from '../utils/formatBrowserError';
import { ASSISTANT_RAIL_BG_CLASS } from '@design-system/components/AiAssistant/assistantPageTheme';
import { BrowserChatPanel } from './BrowserChatPanel';
import { BrowserCommandDock } from './BrowserCommandDock';
import { BrowserSessionRestoredBanner } from './BrowserSessionRestoredBanner';
import { BrowserUnavailableState } from './BrowserUnavailableState';
import { BrowserViewport } from './BrowserViewport';

function extractUrlFromObservation(message: string): string | null {
  const match = message.match(/Now at (https?:\/\/[^\s]+)/i);
  return match?.[1] ?? null;
}

export function BrowserModule() {
  const { data: browserCapability, isLoading: browserCapabilityLoading } = useBrowserCapability();
  const capabilityEnabled = isBrowserCapabilityEnabled(browserCapability, browserCapabilityLoading);
  const { status: providerStatus } = useBrowserProviderStatus(capabilityEnabled);
  const isMobileLayout = useIsMobile();

  const [launch] = useState(() => consumeBrowserLaunch());

  const [currentUrl, setCurrentUrl] = useState<string | undefined>(launch?.customStartUrl);
  const [contextPreview, setContextPreview] = useState<string | undefined>(launch?.context);
  const [suggestedPrompts, setSuggestedPrompts] = useState<string[]>(() => {
    if (launch?.mission) {
      return getBrowserMission(launch.mission)?.prompts ?? [];
    }
    if (launch?.customStartUrl) {
      return promptsForStartUrl(launch.customStartUrl);
    }
    return promptsForStartUrl(BROWSER_QUICK_START_URL);
  });
  const autoRunRef = useRef(false);
  const resumeAttemptedRef = useRef(false);
  const hydratedAuditRef = useRef<string | null>(null);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [pendingRetryInstruction, setPendingRetryInstruction] = useState<string | null>(null);
  const [sessionTimeoutMinutes, setSessionTimeoutMinutes] = useState<number | undefined>();
  const [serverActionCount, setServerActionCount] = useState<number | undefined>();
  const [commandPanelPinned, setCommandPanelPinned] = useState<boolean | null>(null);

  const {
    session,
    isConnecting,
    error,
    resumedSession,
    createSession,
    resumeSession,
    navigateSession,
    destroySession,
    clearSessionLocally,
    patchSession,
  } = useBrowserSession();

  const handleSessionEnded = useCallback(() => {
    clearSessionLocally();
    setSessionExpired(true);
    setCommandPanelPinned(null);
  }, [clearSessionLocally]);

  const onObservation = useCallback((message: string) => {
    const url = extractUrlFromObservation(message);
    if (url) setCurrentUrl(url);
  }, []);

  const { messages, sendMessage, cancelStream, isStreaming, clearMessages, setActiveContext, hydrateFromServerActions } =
    useBrowserChat(
    session?.id ?? null,
    {
      onObservation,
      onSessionLost: (lastInstruction) => {
        handleSessionEnded();
        if (lastInstruction?.trim()) {
          setPendingRetryInstruction(lastInstruction.trim());
        }
      },
    },
  );

  const localActionCount = useMemo(
    () => messages.filter((m) => m.role === 'action').length,
    [messages],
  );

  const displayActionCount = useMemo(() => {
    if (!session) return undefined;
    const server = serverActionCount ?? 0;
    return Math.max(server, localActionCount);
  }, [session, serverActionCount, localActionCount]);

  const awaitingLiveView = useMemo(() => {
    if (!session) return false;
    return !pickEmbedLiveViewUrl(session.liveViewUrl, session.liveViewFullscreenUrl);
  }, [session]);

  useBrowserSessionPoll(
    session?.id ?? null,
    setCurrentUrl,
    Boolean(session),
    handleSessionEnded,
    (meta) => {
      if (meta.timeoutMinutes) setSessionTimeoutMinutes(meta.timeoutMinutes);
      if (typeof meta.actionCount === 'number') setServerActionCount(meta.actionCount);
      if (meta.liveViewUrl) {
        patchSession({ liveViewUrl: meta.liveViewUrl });
      }
      if (meta.liveViewFullscreenUrl) {
        patchSession({ liveViewFullscreenUrl: meta.liveViewFullscreenUrl });
      }
      if (meta.liveViewFallbackUrl) {
        patchSession({ liveViewFallbackUrl: meta.liveViewFallbackUrl });
      }
      if (
        meta.recentActions?.length &&
        session?.id &&
        hydratedAuditRef.current !== session.id &&
        !isStreaming
      ) {
        hydrateFromServerActions(meta.recentActions);
        hydratedAuditRef.current = session.id;
      }
    },
    isStreaming,
    awaitingLiveView,
  );

  useEffect(() => {
    const live = session?.liveViewUrl ?? '';
    if (!session?.id || !needsServerEmbedLiveUrlRefresh(live)) return;
    void fetch(apiV1Url(`/platform/browser/sessions/${encodeURIComponent(session.id)}`), {
      headers: getAuthHeaders(),
      credentials: 'include',
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        const next = data?.session as BrowserSession | undefined;
        if (!next?.liveViewUrl) return;
        patchSession({
          liveViewUrl: next.liveViewUrl,
          liveViewFullscreenUrl: next.liveViewFullscreenUrl,
          liveViewFallbackUrl: next.liveViewFallbackUrl,
        });
      })
      .catch(() => undefined);
  }, [session?.id, session?.liveViewUrl, patchSession]);

  useEffect(() => {
    if (!session?.id) {
      hydratedAuditRef.current = null;
      setServerActionCount(undefined);
    }
  }, [session?.id]);

  useEffect(() => {
    if (session?.id && currentUrl?.trim()) {
      persistBrowserSession(session.id, currentUrl.trim());
    }
  }, [session?.id, currentUrl]);

  const displaySession = useMemo(() => {
    if (!session) return null;
    return { ...session, currentUrl: currentUrl ?? session.currentUrl };
  }, [session, currentUrl]);

  const commandPanelOpen = useMemo(() => {
    if (commandPanelPinned != null) return commandPanelPinned;
    if (!displaySession) return true;
    return !pickEmbedLiveViewUrl(
      displaySession.liveViewUrl,
      displaySession.liveViewFullscreenUrl,
    );
  }, [commandPanelPinned, displaySession]);

  const setCommandPanelOpen = useCallback((open: boolean) => {
    setCommandPanelPinned(open);
  }, []);

  const handleSendCommand = useCallback(
    (text: string, ctx?: string) => {
      sendMessage(text, ctx ?? contextPreview);
    },
    [contextPreview, sendMessage],
  );

  const commandInput = useBrowserCommandInput({
    session: displaySession,
    isStreaming,
    messages,
    onMessage: handleSendCommand,
  });

  const signInRequired = useMemo(
    () =>
      messages.some(
        (m) =>
          (m.role === 'error' || m.role === 'assistant') &&
          /sign-in required/i.test(m.content),
      ),
    [messages],
  );

  const lastUserInstruction = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const msg = messages[i];
      if (msg.role === 'user' && msg.content.trim()) {
        return msg.content.trim();
      }
    }
    return null;
  }, [messages]);

  const canRetryLastCommand = useMemo(() => {
    if (!session || isStreaming || !lastUserInstruction) return false;
    const last = messages[messages.length - 1];
    return last?.role === 'error' && isRecoverableBrowserError(last.content);
  }, [session, isStreaming, lastUserInstruction, messages]);

  const handleRetryLastCommand = useCallback(() => {
    if (!lastUserInstruction) return;
    sendMessage(lastUserInstruction, contextPreview);
  }, [contextPreview, lastUserInstruction, sendMessage]);

  const handleNavigate = useCallback(
    async (url: string) => {
      const result = await navigateSession(url);
      if (result.ok) {
        setCurrentUrl(result.url);
        setSuggestedPrompts(promptsForStartUrl(result.url));
      }
      return result;
    },
    [navigateSession],
  );

  useEffect(() => {
    if (launch?.context) {
      setActiveContext(launch.context);
    }
  }, [launch, setActiveContext]);

  useEffect(() => {
    if (launch || session || isConnecting || resumeAttemptedRef.current) return;
    resumeAttemptedRef.current = true;
    const persisted = readPersistedBrowserSession();
    if (!persisted?.sessionId) return;
    void resumeSession(persisted.sessionId).then((result) => {
      if (!result.ok) {
        if (result.reason === 'not_found') {
          setSessionExpired(true);
        }
        return;
      }
      if (persisted.currentUrl) {
        setCurrentUrl(persisted.currentUrl);
        setSuggestedPrompts(promptsForStartUrl(persisted.currentUrl));
      }
    });
  }, [launch, session, isConnecting, resumeSession]);

  useEffect(() => {
    if (!launch || session || isConnecting) return;
    const startUrl = resolveMissionStartUrl(launch.mission, launch.customStartUrl);
    setCurrentUrl(startUrl);
    createSession(startUrl);
  }, [launch, session, isConnecting, createSession]);

  useEffect(() => {
    if (!session || !launch?.task || autoRunRef.current) return;
    if (!launch.autoRun) return;
    if (isConnecting || awaitingLiveView) return;
    autoRunRef.current = true;
    sendMessage(launch.task, launch.context);
  }, [session, launch, sendMessage, isConnecting, awaitingLiveView]);

  const handleReconnect = useCallback(async () => {
    setSessionExpired(false);
    const startUrl =
      currentUrl?.trim() ||
      launch?.customStartUrl?.trim() ||
      (launch?.mission
        ? resolveMissionStartUrl(launch.mission, launch.customStartUrl)
        : BROWSER_QUICK_START_URL);
    setSuggestedPrompts(promptsForStartUrl(startUrl));
    setCurrentUrl(startUrl);
    await createSession(startUrl, false);
  }, [createSession, currentUrl, launch]);

  const handleReleaseSessionSlots = useCallback(async () => {
    try {
      const res = await fetch(apiV1Url('/platform/browser/sessions/reconcile'), {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
        const message =
          (typeof body.error === 'string' && body.error.trim()) ||
          (typeof body.detail === 'string' && body.detail.trim()) ||
          `Release failed (${res.status})`;
        throw new Error(message);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to release session slots';
      console.error(message);
    }
    const startUrl =
      currentUrl?.trim() ||
      launch?.customStartUrl?.trim() ||
      (launch?.mission
        ? resolveMissionStartUrl(launch.mission, launch.customStartUrl)
        : BROWSER_QUICK_START_URL);
    setSuggestedPrompts(promptsForStartUrl(startUrl));
    setCurrentUrl(startUrl);
    await createSession(startUrl, false);
  }, [createSession, currentUrl, launch]);

  useEffect(() => {
    if (!session?.id || sessionExpired || !pendingRetryInstruction) return;
    const instruction = pendingRetryInstruction;
    setPendingRetryInstruction(null);
    sendMessage(instruction, contextPreview);
  }, [
    session?.id,
    sessionExpired,
    pendingRetryInstruction,
    sendMessage,
    contextPreview,
  ]);

  const handleQuickStart = useCallback(() => {
    const startUrl = BROWSER_QUICK_START_URL;
    setSuggestedPrompts(promptsForStartUrl(startUrl));
    setCurrentUrl(startUrl);
    createSession(startUrl);
  }, [createSession]);

  const handleStartMission = useCallback(
    (mission: BrowserMission) => {
      setSuggestedPrompts(mission.prompts);
      const startUrl = mission.resolveStartUrl();
      setCurrentUrl(startUrl);
      createSession(startUrl, true);
    },
    [createSession],
  );

  const handleRetry = useCallback(() => {
    void handleReconnect();
  }, [handleReconnect]);

  const handleStop = useCallback(async () => {
    await destroySession();
    setCurrentUrl(undefined);
    autoRunRef.current = false;
    setSessionExpired(false);
    setPendingRetryInstruction(null);
    setCommandPanelPinned(null);
  }, [destroySession]);

  if (!capabilityEnabled) {
    return (
      <div className="flex h-full min-h-0 w-full flex-col overflow-hidden bg-gray-50 dark:bg-gray-900">
        <BrowserUnavailableState />
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-gray-50 dark:bg-gray-900">
      {error ? (
        <div
          className={
            isSteelChromiumLaunchError(error)
              ? 'flex flex-wrap items-center justify-between gap-2 border-b border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-100'
              : 'flex flex-wrap items-center justify-between gap-2 border-b border-red-200 bg-red-50 px-4 py-2 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200'
          }
          data-testid={isSteelChromiumLaunchError(error) ? 'browser-chromium-launch-banner' : undefined}
        >
          <span>{formatBrowserSessionError(error)}</span>
          {isMaxSessionsError(error) ? (
            <button
              type="button"
              onClick={() => void handleReleaseSessionSlots()}
              disabled={isConnecting}
              className="rounded-md border border-red-300 px-2 py-1 text-xs font-medium hover:bg-red-100 disabled:opacity-50 dark:border-red-800 dark:hover:bg-red-900/50"
              data-testid="browser-release-session-slots"
            >
              Release stale sessions
            </button>
          ) : (
            <button
              type="button"
              onClick={handleRetry}
              disabled={isConnecting}
              className={
                isSteelChromiumLaunchError(error)
                  ? 'rounded-md border border-amber-300 px-2 py-1 text-xs font-medium hover:bg-amber-100 disabled:opacity-50 dark:border-amber-800 dark:hover:bg-amber-900/50'
                  : 'rounded-md border border-red-300 px-2 py-1 text-xs font-medium hover:bg-red-100 dark:border-red-800 dark:hover:bg-red-900/50'
              }
            >
              {isSteelChromiumLaunchError(error) ? 'Try again' : 'Retry'}
            </button>
          )}
        </div>
      ) : null}
      {providerStatus.provider === 'steel' && providerStatus.steelReachable === false ? (
        <div
          className="flex flex-wrap items-center justify-between gap-2 border-b border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-100"
          data-testid="browser-steel-unreachable-banner"
        >
          <span>
            Steel browser is not reachable. Run <code className="text-xs">make dev-browser-up</code> and
            confirm <code className="text-xs">STEEL_API_URL</code> on platform-service.
          </span>
        </div>
      ) : null}
      {launch?.autoRun && launch?.task && session && !sessionExpired ? (
        <div
          className="border-b border-sky-200 bg-sky-50 px-4 py-2 text-sm text-sky-900 dark:border-sky-800/60 dark:bg-sky-950/40 dark:text-sky-100"
          data-testid="browser-crm-launch-banner"
          role="status"
        >
          {launch.mission
            ? `${getBrowserMission(launch.mission)?.label ?? 'CRM'} task from Brazn — `
            : 'CRM task from Brazn — '}
          {awaitingLiveView || isConnecting
            ? 'waiting for live browser view, then automation starts…'
            : isStreaming
              ? 'automation running…'
              : 'automation started from CRM.'}
        </div>
      ) : null}
      {resumedSession && session ? (
        <BrowserSessionRestoredBanner visible={resumedSession && Boolean(session)} />
      ) : null}
      {sessionExpired && !session ? (
        <div
          className="flex flex-wrap items-center justify-between gap-2 border-b border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-100"
          data-testid="browser-session-expired-banner"
        >
          <span>
            Session ended.
            {pendingRetryInstruction
              ? ' Reconnect to retry your last command automatically.'
              : ' Reconnect to pick up where you left off.'}
          </span>
          <button
            type="button"
            onClick={() => void handleReconnect()}
            disabled={isConnecting}
            className="rounded-md border border-amber-300 px-2 py-1 text-xs font-semibold hover:bg-amber-100 disabled:opacity-50 dark:border-amber-800 dark:hover:bg-amber-900/50"
          >
            Reconnect
          </button>
        </div>
      ) : null}
      <div
        className={
          commandPanelOpen
            ? 'flex h-full min-h-0 flex-1 flex-col overflow-hidden md:flex-row'
            : 'flex h-full min-h-0 flex-1 flex-col overflow-hidden'
        }
      >
        <div className={`flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden max-md:min-h-[50vh] ${ASSISTANT_RAIL_BG_CLASS}`}>
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <BrowserViewport
            session={displaySession}
            isConnecting={isConnecting}
            signInRequired={signInRequired}
            sessionExpired={sessionExpired && !session}
            actionCount={displayActionCount}
            lastKnownUrl={currentUrl}
            commandPanelOpen={commandPanelOpen}
            onToggleCommandPanel={() => setCommandPanelOpen(!commandPanelOpen)}
            onStart={handleQuickStart}
            onStop={handleStop}
            onReconnect={() => void handleReconnect()}
            onStartMission={handleStartMission}
            showMobileMissionPicker={commandPanelOpen && !displaySession && isMobileLayout}
            onLiveViewDisconnected={handleSessionEnded}
            provider={session?.provider ?? providerStatus.provider}
            providerReady={providerStatus.ready}
            providerLabel={
              providerStatus.provider === 'steel'
                ? 'Steel'
                : providerStatus.provider === 'browserbase'
                  ? 'Cloud browser'
                  : undefined
            }
            onNavigate={session ? handleNavigate : undefined}
            />
          </div>
          {!commandPanelOpen ? (
            <BrowserCommandDock
              input={commandInput.input}
              inputPlaceholder={commandInput.inputPlaceholder}
              isDisabled={commandInput.isDisabled}
              isStreaming={isStreaming}
              onInputChange={commandInput.setInput}
              onSend={commandInput.handleSend}
              onExpandPanel={() => setCommandPanelOpen(true)}
              onCancelStream={cancelStream}
            />
          ) : null}
        </div>
        {commandPanelOpen ? (
          <div className="flex min-h-0 min-w-0 max-md:max-h-[280px] shrink-0 grow-0 flex-col overflow-hidden border-gray-300 max-md:border-t dark:border-gray-600 md:h-full md:basis-[400px] md:border-l md:border-t-0">
            <BrowserChatPanel
              messages={messages}
              session={displaySession}
              isStreaming={isStreaming}
              isConnecting={isConnecting}
              suggestedPrompts={suggestedPrompts}
              contextPreview={contextPreview}
              sessionTimeoutMinutes={sessionTimeoutMinutes}
              onDismissContextPreview={() => setContextPreview(undefined)}
              onMessage={handleSendCommand}
              onStartMission={handleStartMission}
              onCancelStream={cancelStream}
              canRetryLastCommand={canRetryLastCommand}
              onRetryLastCommand={handleRetryLastCommand}
              retryLastCommandLabel={
                signInRequired ? "I've signed in — retry command" : 'Retry last command'
              }
              onClearChat={messages.length > 0 && !isStreaming ? clearMessages : undefined}
              sessionExpired={sessionExpired && !session}
              actionCount={displayActionCount}
              commandInput={commandInput}
              onCollapsePanel={() => setCommandPanelOpen(false)}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}
